import { createReducer } from "@reduxjs/toolkit";
import { combineReducers } from "redux";

import referenceAnalysisReducer from "analysis/referenceAnalysisReducer";
import { ReduxActionTypes } from "legacy/constants/ReduxActionConstants";
import apiPaneReducer from "./apiPaneReducer";
import appViewReducer from "./appViewReducer";
import canvasSelectionReducer from "./canvasSelectionReducer";
import widgetDraggingReducer from "./dragResizeReducer";
import dynamicFormReducer from "./dynamicFormReducer";
import editorPreferencesReducer from "./editorPreferencesReducer";
import editorReducer from "./editorReducer";
import errorReducer from "./errorReducer";
import explorerReducer from "./explorerReducer";
import globalReducer from "./global";
import modalReducer from "./modalReducer";
import orgReducer from "./orgReducer";
import propertyPaneReducer from "./propertyPaneReducer";
import themeReducer from "./themeReducer";
import usersReducer from "./usersReducer";
import type { APIPaneReduxState } from "./apiPaneReducer";
import type { AppViewReduxState } from "./appViewReducer";
import type { CanvasSelectionState } from "./canvasSelectionReducer";
import type { WidgetDragResizeState } from "./dragResizeReducer";
import type { DynamicFormState } from "./dynamicFormReducer";
import type { EditorPreferencesState } from "./editorPreferencesReducer";
import type { EditorReduxState } from "./editorReducer";
import type { ErrorReduxState } from "./errorReducer";
import type { ExplorerReduxState } from "./explorerReducer";
import type { GlobalState } from "./global";
import type { ModalState } from "./modalReducer";
import type { OrgReduxState } from "./orgReducer";
import type { PropertyPaneReduxState } from "./propertyPaneReducer";
import type { ThemeState } from "./themeReducer";
import type { UsersReduxState } from "./usersReducer";
import type { ReferenceAnalysisState } from "analysis/referenceAnalysisReducer";
import type { ReduxAction } from "legacy/constants/ReduxActionConstants";

export interface UiState {
  editor: EditorReduxState;
  propertyPane: PropertyPaneReduxState;
  apiPane: APIPaneReduxState;
  errors: ErrorReduxState;
  appView: AppViewReduxState;
  orgs: OrgReduxState;
  users: UsersReduxState;
  widgetDragResize: WidgetDragResizeState;
  explorer: ExplorerReduxState;
  editorPreferences: EditorPreferencesState;
  theme: ThemeState;
  modal: ModalState;
  globalStates: GlobalState;
  referenceAnalysis: ReferenceAnalysisState;
  canvasSelection: CanvasSelectionState;
  isSplitPaneDragging: boolean;
  isAiAssistantDragging: boolean;
  dynamicForm: DynamicFormState;
}

export default combineReducers({
  editor: editorReducer,
  errors: errorReducer,
  propertyPane: propertyPaneReducer,
  appView: appViewReducer,
  orgs: orgReducer,
  users: usersReducer,
  widgetDragResize: widgetDraggingReducer,
  explorer: explorerReducer,
  theme: themeReducer,
  editorPreferences: editorPreferencesReducer,
  apiPane: apiPaneReducer,
  modal: modalReducer,
  globalStates: globalReducer,
  referenceAnalysis: referenceAnalysisReducer,
  canvasSelection: canvasSelectionReducer,
  isSplitPaneDragging: createReducer(false, (builder) =>
    builder.addCase(
      ReduxActionTypes.SET_SPLIT_PANE_DRAGGING,
      (
        state,
        action: ReduxAction<
          boolean,
          typeof ReduxActionTypes.SET_SPLIT_PANE_DRAGGING
        >,
      ) => {
        return action.payload;
      },
    ),
  ),
  isAiAssistantDragging: createReducer(false, (builder) =>
    builder.addCase(
      ReduxActionTypes.SET_AI_ASSISTANT_DRAGGING,
      (
        state: boolean,
        action: ReduxAction<
          boolean,
          typeof ReduxActionTypes.SET_AI_ASSISTANT_DRAGGING
        >,
      ) => {
        return action.payload;
      },
    ),
  ),
  dynamicForm: dynamicFormReducer,
});
